import type { MallSlot, MallWindow } from "../net/messages";
import type { BagModel } from "./bag";
import { itemArtNode } from "./itemart";
import { iconCellsTall } from "./itemicon";
import { ItemTooltip, attachHoverTooltip, wireTooltipData, type ItemTooltipData } from "./tooltip";
import { elem, installChrome, makeDraggable, styleInstaller } from "./chrome";
import { SAFEBOX_COLUMNS, SAFEBOX_ROWS } from "./safebox";

export interface MallActions {
  /** Move the item in this mall cell into this bag cell. */
  onCheckout?: (cell: number, slot: number) => void;
  onClose?: () => void;
}

export function firstFreeCell(bag: BagModel): number | null {
  const taken = new Set(bag.slots.map((slot) => slot.slot));
  for (let cell = 0; cell < bag.capacity; cell += 1) {
    if (!taken.has(cell)) return cell;
  }
  return null;
}

const STYLE_ID = "mall-style";

/** `MALL_TITLE` (`locale_interface.txt:189`). */
export const MALL_TITLE = "Item Shop Storeroom";
const SLOT_STEP = 32; // uisafebox.py:578
const MALL_CELLS = SAFEBOX_COLUMNS * SAFEBOX_ROWS; // uisafebox.py:576-578
export const MALL_WIDTH = 176; // uisafebox.py:501 BOX_WIDTH
export const MALL_HEIGHT = 85 + SLOT_STEP * SAFEBOX_ROWS; // uisafebox.py:583
const LARGE_BUTTON_WIDTH = 88; // large_button_01.sub
const EXIT_BOTTOM = 37; // mallwindow.py:52

const STYLE = `
/* MallWindow at (100, 20) (mallwindow.py:6-7). */
.mall {
  position: fixed;
  left: 100px;
  top: 20px;
  z-index: 61;
  width: ${MALL_WIDTH}px;
  height: ${MALL_HEIGHT}px;
  padding: 0;
  margin: 0;
  pointer-events: auto;
}
/* TitleBar at (8, 7), 161 wide (mallwindow.py:31-37). */
.mall > .m2-titlebar { left: 8px; top: 7px; right: auto; width: 161px; }
.mall > .m2-close { z-index: 4; }
/* wndItem at (8, 35), five by nine 32px slots (uisafebox.py:546, :578). */
.mall-grid {
  position: absolute;
  left: 8px;
  top: 35px;
  display: grid;
  grid-template-columns: repeat(${SAFEBOX_COLUMNS}, ${SLOT_STEP}px);
  grid-template-rows: repeat(${SAFEBOX_ROWS}, ${SLOT_STEP}px);
}
.mall-cell { position: relative; display: block; overflow: visible; }
.mall-cell .item-icon {
  position: absolute;
  left: 0;
  top: 0;
  width: ${SLOT_STEP}px;
  height: calc(${SLOT_STEP}px * var(--mall-item-span, 1));
  margin: 0;
  pointer-events: none;
  z-index: 1;
}
.mall-glyph {
  position: absolute;
  left: 4px;
  top: 4px;
  width: 24px;
  height: 24px;
  pointer-events: none;
  z-index: 1;
}
.mall-count {
  position: absolute;
  right: 1px;
  bottom: 0;
  z-index: 2;
  font: 10px/1 Tahoma, "MS Sans Serif", sans-serif;
  color: #fff6d0;
  text-shadow: 1px 1px 0 #000;
  pointer-events: none;
}
/* ExitButton, centred, 37 from the bottom (mallwindow.py:46-58). */
.mall-exit {
  position: absolute;
  left: ${Math.floor(MALL_WIDTH / 2) - Math.floor(LARGE_BUTTON_WIDTH / 2)}px;
  top: ${MALL_HEIGHT - EXIT_BOTTOM}px;
}
`;

const installStyle = styleInstaller(STYLE_ID, STYLE, installChrome);

function cellTooltipData(cell: MallSlot): ItemTooltipData {
  return wireTooltipData(cell, { price: 0 });
}

function cellLabel(held: MallSlot): string {
  return held.count > 1 ? `${held.name} x${held.count}` : held.name;
}

/** `MallWindow` (`uisafebox.py:499-640`): the storeroom's grid, one page, with
 *  a title bar and a Close button. A click on an item takes it out into the
 *  first free bag cell. */
export class MallPanel {
  private readonly root: HTMLElement;
  private readonly cells: HTMLButtonElement[] = [];
  /** The mall item under each cell, covered cells included. */
  private readonly heldAt: (MallSlot | undefined)[] = Array.from({ length: MALL_CELLS });
  private mall: MallWindow | null = null;
  private bag: BagModel | null = null;
  private readonly tooltip: ItemTooltip;

  constructor(
    container: HTMLElement,
    private readonly actions: MallActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);
    this.tooltip = new ItemTooltip(doc);

    this.root = elem(doc, "div", "mall m2-board");
    this.root.hidden = true;
    this.root.setAttribute("role", "dialog");
    this.root.setAttribute("aria-modal", "false");

    const title = elem(doc, "div", "m2-titlebar mall-title", MALL_TITLE);
    makeDraggable(this.root, title);

    // The title bar's X and the Close button both run `Close` (uisafebox.py:556-557).
    const close = doc.createElement("button");
    close.type = "button";
    close.className = "m2-close mall-close";
    close.setAttribute("aria-label", "Close");
    close.addEventListener("click", () => this.dismiss());

    const grid = elem(doc, "div", "mall-grid");
    for (let i = 0; i < MALL_CELLS; i++) {
      const cell = doc.createElement("button");
      cell.type = "button";
      cell.className = "mall-cell m2-slot";
      const local = i;
      attachHoverTooltip(cell, this.tooltip, () => {
        const held = this.heldAt[local];
        return held === undefined ? null : cellTooltipData(held);
      });
      cell.addEventListener("click", () => this.checkout(local));
      this.cells.push(cell);
      grid.append(cell);
    }

    const exit = doc.createElement("button");
    exit.type = "button";
    exit.className = "mall-exit m2-large";
    exit.textContent = "Close"; // locale_interface.txt:16
    exit.addEventListener("click", () => this.dismiss());

    this.root.append(title, close, grid, exit);
    container.append(this.root);
  }

  /** The bag, for working out where a checked-out item would go. */
  setBag(bag: BagModel): void {
    this.bag = bag;
  }

  /** Draw what the server sent. */
  update(mall: MallWindow): void {
    this.mall = mall;
    this.drawCells();
  }

  open(): void {
    this.root.hidden = false;
    this.drawCells();
  }

  hide(): void {
    this.root.hidden = true;
    this.tooltip.hide();
  }

  toggle(): void {
    if (this.showing) this.hide();
    else this.open();
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  private dismiss(): void {
    this.actions.onClose?.();
    this.hide();
  }

  private checkout(local: number): void {
    const held = this.heldAt[local];
    if (held === undefined) return;
    // A full bag sends nothing: the server would refuse a cell that is not free.
    const destination = this.bag === null ? null : firstFreeCell(this.bag);
    if (destination === null) return;
    this.actions.onCheckout?.(held.cell, destination);
  }

  private drawCells(): void {
    const doc = this.root.ownerDocument;
    for (let i = 0; i < MALL_CELLS; i++) {
      const cell = this.cells[i];
      this.heldAt[i] = undefined;
      cell.replaceChildren();
      cell.className = "mall-cell m2-slot";
      cell.title = "";
      cell.removeAttribute("aria-label");
      cell.style.removeProperty("--mall-item-span");
    }
    for (const held of this.mall?.cells ?? []) {
      if (held.cell < 0 || held.cell >= MALL_CELLS) continue;
      const row = Math.floor(held.cell / SAFEBOX_COLUMNS);
      const span = Math.max(1, Math.min(iconCellsTall(held.vnum), SAFEBOX_ROWS - row));
      const label = cellLabel(held);
      for (let y = 0; y < span; y++) {
        const index = held.cell + y * SAFEBOX_COLUMNS;
        this.heldAt[index] = held;
        this.cells[index].title = label;
        if (y > 0) this.cells[index].classList.add("mall-covered");
      }
      const cell = this.cells[held.cell];
      cell.classList.add("mall-item");
      cell.setAttribute("aria-label", label);
      cell.style.setProperty("--mall-item-span", String(span));
      cell.append(itemArtNode(held.vnum, held.kind, doc, "mall-glyph"));
      // `RefreshMall` passes a count of one as zero, drawn as no number
      // (uisafebox.py:594-595).
      if (held.count > 1) cell.append(elem(doc, "span", "mall-count", String(held.count)));
    }
  }
}
